<template>
  <div
    class="green-operation-assessment"
    :class="{'is-edit': isEdit}"
  >
    <HKLLeftRight class="green-operation-assessment__header">
      <template #left>
        <div
          class="green-operation-letter__filter-bar align-middle"
        >
          <div class="green-operation-letter__filter-label">
            {{ $t('showLabel') }}:
          </div>
          <div class="green-operation-letter__filter-selector">
            <HKLSelect
              class="no-border flex-theme"
              :showArrow="!isEdit"
              :value="selectedLetterId"
              :options="letterOptions"
              :open="isEdit ? false : undefined"
              @select="selectLetter"
            />
          </div>
        </div>
      </template>
      <template
        v-if="isCurrentLetter && !readonly"
        #right
      >
        <template v-if="isEdit">
          <HKLButton
            color="darkblue"
            action="cancel"
            @click="() => cancel()"
          />
          <HKLButton
            color="orange"
            action="save"
            @click="() => save()"
          />
        </template>
        <template v-else>
          <HKLButton
            v-if="canReview"
            color="orange"
            action="edit"
            @click="() => setEdit(true)"
          />
        </template>
      </template>
    </HKLLeftRight>
    <HKLForm ref="form">
      <component
        :is="componentVue"
        v-for="(componentVue, index) in assessmentComponents"
        :key="index"
      />
    </HKLForm>
  </div>
</template>

<script>
import { updateOperationMetadata } from '@/api/tenancies/GreenOperation.request'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLForm from '@/controls/Form/HKLForm.vue'
import { useRefForm } from '@/controls/Form/hook'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { whenever } from '@vueuse/core'
import { computed, unref } from 'vue-demi'
import { useGreenOperationLetters } from './GreenOperationLetter.hook'
import AirConditioning from './assessments/AirConditioning.vue'
import ElectricityData from './assessments/ElectricityData.vue'
import GreenBuildingCertificate from './assessments/GreenBuildingCertificate.vue'
import GreenFitOutVue from './assessments/GreenFitOut.vue'
import LightingSystem from './assessments/LightingSystem.vue'
import NoBottled from './assessments/NoBottled.vue'
import WasteAndRecycling from './assessments/WasteAndRecycling.vue'
import { filterByLetterId, useSelectedLetterId } from './assessments/hook'
import { GREEN_OPERATION_LETTER_STATUS } from './consts'
import { useGreenOperation, useGreenOperationAssessmentFormStatus, useGreenOperationPerms } from './hook'
import lodash from 'lodash'

export default {
  components: {
    HKLSelect,
    HKLLeftRight,
    HKLButton,
    HKLForm,
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const { data, refresh } = useGreenOperation()
    const { form, validate } = useRefForm()
    const assessmentComponents = computed(() => [
      AirConditioning,
      LightingSystem,
      WasteAndRecycling,
      ElectricityData,
      GreenBuildingCertificate,
      GreenFitOutVue,
      NoBottled,
    ])
    const { isEdit, setEdit } = useGreenOperationAssessmentFormStatus()
    const { canReview } = useGreenOperationPerms()

    const { data: letters, isSuccess } = useGreenOperationLetters()
    const currentLetterId = ''
    const { selectedLetterId, selectLetter, isCurrentLetter } = useSelectedLetterId()
    whenever(isSuccess, () => {
      selectLetter(unref(currentLetterId))
    }, { immediate: true })
    const letterOptions = computed(() => {
      const completedLetters = lodash.orderBy(unref(letters), 'createdAt', 'desc').filter(item => item.status === GREEN_OPERATION_LETTER_STATUS.COMPLETED)
      const total = completedLetters.length
      const result = completedLetters.map((item, index) => {
        const [startDate = null, endDate = null] = item.duration
        const name = i18n.t('tenancy.greenOperation.recognisedOf', [
          total - index,
          dateUtil.formatByLongDate(startDate, 'LL'),
          dateUtil.formatByLongDate(endDate, 'LL'),
        ])
        return {
          name,
          value: item.id,
        }
      })
      result.unshift({
        name: i18n.t('tenancy.greenOperation.lastAssessment'),
        value: currentLetterId,
      })
      return result
    })
    return {
      letters,
      currentLetterId,
      isCurrentLetter,
      selectLetter,
      letterOptions,
      selectedLetterId,
      assessmentComponents,
      isEdit,
      setEdit,
      canReview,
      data,
      form,
      cancel: async () => {
        setEdit(false)
        await refresh()
      },
      save: async () => {
        if (!validate()) return
        const id = unref(data).id
        const selectedAssessments = unref(data).assessments.filter(filterByLetterId(selectedLetterId))
        const metadata = selectedAssessments
          .reduce((rs, { type, metadata }) => {
            rs[type] = metadata
            return rs
          }, {})
        const submittedMetadata = selectedAssessments
          .reduce((rs, { type, submittedMetadata }) => {
            rs[type] = submittedMetadata
            return rs
          }, {})
        await updateOperationMetadata(id, {
          metadata,
          submittedMetadata,
          letterId: unref(selectedLetterId),
        })
        await refresh()
        setEdit(false)
      },
    }
  },
}
</script>
<style lang="scss" scoped>
.green-operation-assessment__header {
  margin-bottom: 5px;
}
.green-operation-letter__filter {
  &-bar {
    display: flex;
  }
  &-label {
    margin-right: 5px;
  }
  &-selector {
    width: 355px;
    .is-edit & {
      width: 250px;
      & ::v-deep .ant-select-selection--single {
        cursor: initial;
      }
    }
  }
}
.hkl-button {
  & + .hkl-button {
    margin-left: 5px;
  }
}
</style>
